import React from 'react';
import { useBooking } from '../context/BookingContext';

const BookingSidebar = ({ onContinue }) => {
  const { state, formatCurrency, formatDisplayDate, calculateNights } = useBooking();

  const nights = calculateNights();
  const totalQtyInCart = state.cart.reduce((acc, item) => acc + item.quantity, 0);
  const requiredRooms = state.rooms.length;

  const isCheckoutEnabled = totalQtyInCart === requiredRooms && totalQtyInCart > 0;

  const [expandedSections, setExpandedSections] = React.useState({
    bookingDetails: true,
    selectedRooms: true,
    priceBreakdown: true,
    taxDetails: false
  });

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Calculate tax percentage for display
  const calculateTaxPercentage = () => {
    if (!state.subtotal || state.subtotal === 0) return '0%';
    
    const taxPercentage = (state.taxes / state.subtotal * 100).toFixed(1);
    return `${taxPercentage}%`;
  };

const calculateDisplayTotals = () => {
  const nights = calculateNights();
  
  if (state.taxBreakdown && state.taxBreakdown.perRoomBreakdowns) {
    // Calculate cumulative totals from all rooms
    let totalBaseAmount = 0;
    let totalCGST = 0;
    let totalSGST = 0;
    let totalCess = 0;
    let grandTotal = 0;
    let totalPerNightRate = 0;
    
    // Calculate per night rate from cart (for display purposes)
    totalPerNightRate = state.cart.reduce((sum, item) => 
      sum + (item.roomPrice * item.quantity), 0
    );
    
    // Calculate tax totals from all room breakdowns
    state.taxBreakdown.perRoomBreakdowns.forEach(room => {
      if (room.taxBreakdown) {
        totalBaseAmount += room.taxBreakdown.base_amount_total || 0; // This is already for the entire stay
        totalCGST += room.taxBreakdown.total_cgst_amount_after_tax || 0;
        totalSGST += room.taxBreakdown.total_sgst_amount_after_tax || 0;
        totalCess += room.taxBreakdown.total_cess_amount || 0;
        grandTotal += room.taxBreakdown.grand_total || 0;
      }
    });
    
    const totalTaxes = totalCGST + totalSGST + totalCess;
    
    return {
      roomRate: totalBaseAmount, // This is already total for stay (e.g., 3300 + 3300 = 6600)
      perNightRate: totalPerNightRate, // This is per night total (e.g., 1100 + 1100 = 2200)
      subtotal: totalBaseAmount, // Same as roomRate since no discount
      taxes: totalTaxes,
      total: grandTotal,
      finalTotal: grandTotal,
      cgst: totalCGST,
      sgst: totalSGST,
      cess: totalCess,
      nights: nights
    };
  }
  
  // Fallback
  return {
    roomRate: state.rateTotal || 0,
    perNightRate: state.cart.reduce((sum, item) => sum + (item.roomPrice * item.quantity), 0),
    subtotal: state.subtotal || 0,
    taxes: state.taxes || 0,
    total: state.total || 0,
    finalTotal: state.finalTotal || 0,
    nights: calculateNights()
  };
};

const displayTotals = calculateDisplayTotals();


const getCumulativeDisplay = () => {
  const nights = calculateNights();
  let displayText = '';
  
  if (state.taxBreakdown && state.taxBreakdown.perRoomBreakdowns) {
    const totalRooms = state.taxBreakdown.perRoomBreakdowns.length;
    const roomRateTotal = displayTotals.roomRate;
    const taxTotal = displayTotals.taxes;
    const grandTotal = displayTotals.finalTotal;
    
    displayText = `Room Rate (${nights} nights) ${formatCurrency(roomRateTotal)}`;
    
    if (totalRooms > 1) {
      // Show breakdown per room
      state.taxBreakdown.perRoomBreakdowns.forEach((room, index) => {
        displayText += `\nRoom ${index + 1}: ${formatCurrency(room.taxBreakdown.base_amount_total)} + ${formatCurrency((room.taxBreakdown.total_cgst_amount_after_tax || 0) + (room.taxBreakdown.total_sgst_amount_after_tax || 0))} tax`;
      });
    }
    
    displayText += `\nSubtotal: ${formatCurrency(displayTotals.subtotal)}`;
    displayText += `\nTaxes & Fees: ${formatCurrency(taxTotal)}`;
    displayText += `\nTotal: ${formatCurrency(grandTotal)}`;
  }
  
  return displayText;
};

  // Get tax breakdown components
const renderTaxBreakdown = () => {
  if (!state.taxBreakdown || !displayTotals.cgst) {
    const taxPercentage = displayTotals.subtotal > 0 
      ? ((displayTotals.taxes / displayTotals.subtotal) * 100).toFixed(1)
      : '0';
    
    return (
      <div className="flex justify-between text-sm">
        <span className="text-text-secondary">Taxes & Fees ({taxPercentage}%)</span>
        <span className="font-medium text-text-primary">
          {formatCurrency(displayTotals.taxes)}
        </span>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex justify-between text-sm">
        <span className="text-text-secondary">Base Amount</span>
        <span className="font-medium text-text-primary">
          {formatCurrency(displayTotals.subtotal)}
        </span>
      </div>
      
      <div className="flex justify-between text-sm pl-3">
        <span className="text-text-secondary">CGST (3.00%)</span>
        <span className="font-medium text-text-primary">
          {formatCurrency(displayTotals.cgst)}
        </span>
      </div>
      
      <div className="flex justify-between text-sm pl-3">
        <span className="text-text-secondary">SGST (3.00%)</span>
        <span className="font-medium text-text-primary">
          {formatCurrency(displayTotals.sgst)}
        </span>
      </div>
      
      {displayTotals.cess > 0 && (
        <div className="flex justify-between text-sm pl-3">
          <span className="text-text-secondary">Cess</span>
          <span className="font-medium text-text-primary">
            {formatCurrency(displayTotals.cess)}
          </span>
        </div>
      )}
      
      <div className="flex justify-between text-sm pt-2 border-t border-gray-100">
        <span className="text-text-secondary font-semibold">
          Total Taxes & Fees ({state.taxBreakdown.totalTaxPercentage || '0.00'}%)
        </span>
        <span className="font-bold text-text-primary">
          {formatCurrency(displayTotals.taxes)}
        </span>
      </div>
    </div>
  );
};

// Update the price breakdown section in the JSX
<div className="flex justify-between text-sm mb-2 lg:mb-1">
  <span className="text-text-secondary">Room Rate ({nights} night{nights !== 1 ? 's' : ''})</span>
  <span className="font-medium text-text-primary">
    {formatCurrency(displayTotals.roomRate)}
  </span>
</div>

{/* Show how many rooms are included */}
{state.cart.length > 0 && (
  <div className="text-xs text-gray-500 mb-2">
    Includes {state.cart.reduce((acc, item) => acc + item.quantity, 0)} room(s)
  </div>
)}

  // Group cart items by room number for better display
  const getRoomDisplayInfo = () => {
  const roomInfo = [];
  const nights = calculateNights();
  
  state.cart.forEach((item) => {
    for (let i = 0; i < item.quantity; i++) {
      // Find this room's tax breakdown if available
      let roomTotalForStay = 0;
      let roomTaxForStay = 0;
      
      if (state.taxBreakdown && state.taxBreakdown.perRoomBreakdowns) {
        const roomBreakdown = state.taxBreakdown.perRoomBreakdowns.find(
          rb => rb.roomName === item.roomName
        );
        if (roomBreakdown && roomBreakdown.totalTax) {
          roomTotalForStay = roomBreakdown.totalTax.grand_total || 0;
          roomTaxForStay = (roomBreakdown.totalTax.total_cgst_amount_after_tax || 0) + 
                          (roomBreakdown.totalTax.total_sgst_amount_after_tax || 0) + 
                          (roomBreakdown.totalTax.total_cess_amount || 0);
        }
      }
      
      roomInfo.push({
        roomNumber: item.roomNumber || roomInfo.length + 1,
        roomName: item.roomName,
        rateName: item.rateName,
        roomPrice: item.roomPrice, // Per night price
        roomTotalForStay: roomTotalForStay || (item.roomPrice * nights), // Total for stay
        roomTaxForStay: roomTaxForStay,
        offerName: item.offerName,
        adultsCount: item.adultsCount || item.guestInfo?.adults || 1,
        childrenCount: item.childrenCount || item.guestInfo?.children || 0,
        guestInfo: item.guestInfo
      });
    }
  });
  
  return roomInfo;
};

  const roomDisplayInfo = getRoomDisplayInfo();


  // Add these to your state or within the component
const [isBreakdownOpen, setIsBreakdownOpen] = React.useState(false);

const getGroupedRoomInfo = () => {
  const groups = {};
  
  state.cart.forEach((item) => {
    if (!groups[item.roomName]) {
      groups[item.roomName] = {
        name: item.roomName,
        count: 0,
        adults: 0,
        children: 0,
        totalBasePrice: 0
      };
    }
    groups[item.roomName].count += item.quantity;
    groups[item.roomName].adults += (item.adultsCount || 0) * item.quantity;
    groups[item.roomName].children += (item.childrenCount || 0) * item.quantity;
    groups[item.roomName].totalBasePrice += item.roomPrice * item.quantity * nights;
  });
  
  return Object.values(groups);
};

const groupedRooms = getGroupedRoomInfo();

  return (
    <div className="lg:sticky top-24">
      <div className="bg-white shadow-lg lg:shadow-md rounded-xl lg:rounded-lg p-5 lg:p-4 border border-gray-100">
        {/* <p className="text-xs text-text-secondary uppercase font-semibold tracking-wide">Total Amount</p>
        <p className="text-3xl lg:text-2xl font-bold text-text-primary my-1">
            {formatCurrency(displayTotals.finalTotal)}
        </p>
        <p className="text-xs text-text-secondary mb-5 lg:mb-4">For {nights} night{nights !== 1 ? 's' : ''}</p> */}

        <div className="space-y-4 lg:space-y-3">
       {/* Stay Details */}
<div className="pb-4 lg:pb-3 border-b border-gray-200">
  <button
    onClick={() => toggleSection('bookingDetails')}
    className="flex items-center justify-between w-full text-left lg:cursor-default mb-2"
  >
    <h4 className="text-xs font-semibold text-text-primary uppercase tracking">Stay Details</h4>
    <svg
      className={`lg:hidden w-4 h-4 text-text-secondary transition-transform duration-200 ${
        expandedSections.bookingDetails ? 'rotate-180' : ''
      }`}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
    </svg>
  </button>

  <div
    className={`
      lg:block overflow-hidden transition-all duration-300 ease-in-out
      ${expandedSections.bookingDetails ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0 lg:max-h-none lg:opacity-100'}
    `}
  >
    <div className="space-y-3">
      {/* Check-in Row */}
      <div className="flex justify-between items-start">
        <div className="flex flex-col">
          <span className="text-[10px] uppercase font-bold text-text-secondary tracking-tight">Check-in</span>
          <span className="text-sm font-bold text-text-primary">
            {formatDisplayDate(state.searchState.checkin)}
          </span>
          <span className="text-[11px] text-text-secondary">
             {new Date(state.searchState.checkin).toLocaleDateString('en-US', { weekday: 'long' })} • 02:00 PM
          </span>
        </div>
        
        {/* Total Nights Badge */}
        <div className="self-center px-2 py-1 bg-gray-50 border border-gray-100 rounded text-[11px] font-bold text-brand-primary">
          {nights} {nights === 1 ? 'Night' : 'Nights'}
        </div>

        <div className="flex flex-col text-right">
          <span className="text-[10px] uppercase font-bold text-text-secondary tracking-tight">Check-out</span>
          <span className="text-sm font-bold text-text-primary">
            {formatDisplayDate(state.searchState.checkout)}
          </span>
          <span className="text-[11px] text-text-secondary">
            {new Date(state.searchState.checkout).toLocaleDateString('en-US', { weekday: 'long' })} • 11:00 AM
          </span>
        </div>
      </div>
      
      {/* Summary Footer */}
      {/* <div className="pt-2 border-t border-gray-50 flex justify-between items-center text-xs">
         <span className="text-text-secondary">Total Guests</span>
         <span className="font-bold text-text-primary">{state.searchState.guestCount} Adults & Children</span>
      </div> */}
    </div>
  </div>
</div>
          

     {/* Selected Rooms Section */}
<div className="pb-3 border-b border-gray-100">
  {/* Header: Rooms on left, Adults/Children on right */}
  <div className="flex justify-between items-end mb-2">
    <h4 className="text-xs font-bold text-text-primary uppercase tracking ">
      Guest & Rooms 
    </h4>
    {state.cart.length > 0 && (
      <div className="text-right leading-none">
        <span className="text-sm font-semibold text-text-primary">
          {totalQtyInCart} Rooms
        </span>
        <span className="mx-1 text-gray-300">•</span>
        <span className="text-sm font-semibold text-text-primary">
          {groupedRooms.reduce((acc, g) => acc + g.adults, 0)} Adults, {groupedRooms.reduce((acc, g) => acc + g.children, 0)} Children
        </span>
      </div>
    )}
  </div>

  <div className={`lg:block overflow-hidden transition-all duration-300 ease-in-out ${
    expandedSections.selectedRooms ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'
  }`}>
    <div className="space-y-1">
      {/* Each Room Row */}
      {groupedRooms.map((group, idx) => (
        <div key={idx} className="flex justify-between items-center py-1 group">
          {/* Room Name - Truncates if too long to prevent pushing the guests down */}
          <h5 className="font-medium text-sm text-text-primary truncate pr-4">
            {group.count} × {group.name}
          </h5>
          
          {/* Guest Count - Forced to stay on one line */}
          <div className="text-[13px] font-medium text-text-secondary whitespace-nowrap flex-shrink-0">
            {group.adults} Adults, {group.children} Children
          </div>
        </div>
      ))}

      {/* Validation - Slim & Professional */}
      {/* {state.cart.length > 0 && totalQtyInCart !== requiredRooms && (
        <div className={`mt-2 py-1 px-2 rounded border-l-2 flex items-center justify-between ${
          totalQtyInCart < requiredRooms 
            ? 'bg-blue-50 border-blue-500 text-blue-700' 
            : 'bg-red-50 border-red-500 text-red-700'
        }`}>
          <p className="text-[10px] font-bold uppercase tracking-tight">
            {totalQtyInCart < requiredRooms 
              ? `Selection Incomplete: Add ${requiredRooms - totalQtyInCart} Room${requiredRooms - totalQtyInCart > 1 ? 's' : ''}`
              : `Limit Exceeded: Remove ${totalQtyInCart - requiredRooms} Room${totalQtyInCart - requiredRooms > 1 ? 's' : ''}`
            }
          </p>
        </div>
      )} */}
    </div>
  </div>
</div>
       {/* Price Summary */}
<div className=" border-gray-100">
  <div className="space-y-2">
     <h4 className="text-xs font-bold text-text-primary uppercase tracking ">
      Price
    </h4>
    {/* Tariff Row */}
    <div className="flex justify-between text-sm">
      <h5 className="text-text-primary font-medium">
        Tariff for {nights} night{nights !== 1 ? 's' : ''}
      </h5>
      <span className=" text-text-primary ">
        {formatCurrency(displayTotals.subtotal)}
      </span>
    </div>

    {/* Taxes Row */}
    <div className="flex justify-between text-sm">
      <h5 className="text-text-primary font-medium">Taxes</h5>
      <span className=" text-text-primary">
        {formatCurrency(displayTotals.taxes)}
      </span>
    </div>

    {/* Total Payable Row */}
    <div className="flex justify-between items-start pt-3 mt-1 border-t border-gray-100">
      <span className="text-sm font-bold text-text-primary uppercase tracking-tight mt-1">
        Total Payable
      </span>
      <div className="text-right">
        <span className="text-xl font-bold text-text-primary block leading-none">
          {formatCurrency(displayTotals.finalTotal)}
        </span>
        
        {/* View Summary Link - Placed below the total amount */}
        {state.cart.length > 0 && (
          <button 
            onClick={() => setIsBreakdownOpen(true)}
            className="text-[11px] font-bold text-brand-primary underline mt-1 hover:text-brand-primary-hover transition-colors"
          >
            View Summary
          </button>
        )}
      </div>
    </div>
  </div>
</div>

{/* Checkout Section */}
<div className="mt-6">
  {/* Validation Message - Red text only, no background, above button */}
  {state.cart.length > 0 && totalQtyInCart !== requiredRooms && (
    <div className="mb-3 text-center">
      <p className="text-[12px] font-bold text-red-600 uppercase tracking-wide">
        {totalQtyInCart < requiredRooms 
          ? `Please Add ${requiredRooms - totalQtyInCart} More Room${requiredRooms - totalQtyInCart > 1 ? 's' : ' to continue' }`
          : `Limit Exceeded: Remove ${totalQtyInCart - requiredRooms} Room${totalQtyInCart - requiredRooms > 1 ? 's' : ''}`
        }
      </p>
    </div>
  )}

  <button
    onClick={onContinue}
    disabled={!isCheckoutEnabled}
    className={`w-full bg-brand-accent text-text-dark-on-accent px-6 py-3.5 rounded-lg font-semibold text-base hover:bg-brand-accent-hover transition-all duration-200 active:scale-[0.98] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-accent shadow-md ${
      !isCheckoutEnabled ? 'opacity-50 cursor-not-allowed grayscale-[0.5]' : ''
    }`}
  >
    Continue to Checkout
  </button>
</div>

{/* Breakdown Modal */}
{isBreakdownOpen && (
  <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
    <div className="bg-white rounded-2xl w-full max-w-md overflow-hidden shadow-2xl animate-in fade-in zoom-in duration-200">
      {/* Header */}
      <div className="p-4 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
        <h3 className="font-bold text-base text-text-primary">Price Breakup Detail</h3>
        <button 
          onClick={() => setIsBreakdownOpen(false)}
          className="p-1.5 hover:bg-gray-200 rounded-full transition-colors text-text-secondary"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
      
      {/* Content */}
      <div className="p-5 max-h-[60vh] overflow-y-auto">
        <div className="space-y-4">
          
          {/* Room-wise List */}
          <div className="space-y-3">
            <h4 className="text-[10px] font-bold text-text-secondary uppercase tracking-[0.15em] mb-2">Selected Accommodations</h4>
            {roomDisplayInfo.map((room, index) => (
              <div key={index} className="flex justify-between items-start py-2 border-b border-gray-50 last:border-0">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] font-bold bg-gray-100 text-text-secondary px-1.5 py-0.5 rounded">Room {index + 1}</span>
                    <h5 className="font-bold text-sm text-text-primary">{room.roomName}</h5>
                  </div>
                  <p className="text-[11px] text-brand-primary font-semibold mt-1 pl-8">
                    Plan: {room.rateName}
                  </p>
                  <p className="text-[11px] text-text-secondary pl-8">
                    {room.adultsCount} Adults, {room.childrenCount} Children
                  </p>
                </div>
                <div className="text-right">
                  <span className="font-bold text-sm text-text-primary">
                    {formatCurrency(room.roomPrice * nights)}
                  </span>
                  <p className="text-[10px] text-text-secondary mt-0.5">
                    {nights} {nights === 1 ? 'night' : 'nights'}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Financial Summary */}
          <div className="mt-6 pt-4 border-t border-gray-200 space-y-2">
            {/* <div className="flex justify-between text-sm text-text-secondary">
              <span>Room Subtotal</span>
              <span className="font-medium text-text-primary">{formatCurrency(displayTotals.subtotal)}</span>
            </div> */}
            
            <div className="flex justify-between text-sm text-text-secondary">
              <span>Taxes & Fees </span>
              <span className="font-medium text-text-primary">{formatCurrency(displayTotals.taxes)}</span>
            </div>

            <div className="flex justify-between items-center pt-3 mt-1 border-t-2 border-gray-100">
              <span className="text-base font-bold text-text-primary">Grand Total</span>
              <span className="text-xl font-bold text-text-primary">
                {formatCurrency(displayTotals.finalTotal)}
              </span>
            </div>
          </div>
        </div>
      </div>
      
      {/* Footer */}
      <div className="p-4 pt-2">
        <button 
          onClick={() => setIsBreakdownOpen(false)}
          className="w-full py-3 bg-text-primary text-white rounded-xl font-bold shadow-md active:scale-[0.98] transition-all"
        >
          Close Detail View
        </button>
      </div>
    </div>
  </div>
)}

          {/* Continue Button */}
          {/* <div className="pt-3 lg:pt-2">
            <button
              onClick={onContinue}
              disabled={!isCheckoutEnabled}
              className={`w-full bg-brand-accent text-text-dark-on-accent px-6 py-3.5 lg:py-2 rounded-lg lg:rounded-md font-semibold text-base hover:bg-brand-accent-hover transition-all duration-200 active:scale-[0.98] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-accent shadow-lg lg:shadow-none ${
                !isCheckoutEnabled ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              Continue to Checkout
            </button>
          </div> */}
        </div>
      </div>
    </div>
  );
};

export default BookingSidebar;